import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { User, Alarm, AlarmSet, Class } from '../types/alarm';
import { saveClassAlarmSets, loadCurrentClassId } from '../utils/storage';
import { userDataService } from '../services/userDataService';
import { sortAlarmsByTime, getDayName, preloadAlarmSounds } from '../utils/alarmUtils';
import { checkAlarmSetLimit } from '../utils/licenseLimits';
import Header from '../components/Header';
import AlarmBlock from '../components/AlarmBlock';
import NewAlarmModal from '../components/NewAlarmModal';
import SetNameModal from '../components/SetNameModal';
import EditSetNameModal from '../components/EditSetNameModal';
import CopyDayModal from '../components/CopyDayModal';
import ConfirmModal from '../components/ConfirmModal';
import LicenseLimitModal from '../components/LicenseLimitModal';
import { hideScrollbar } from '../styles/hideScrollbar';

interface AlarmPageProps {
  user: User;
  onLogout: () => void;
  onNavigate: (page: 'home' | 'alarm' | 'admin' | 'assignments' | 'points-table' | 'chat' | 'board' | 'license' | 'terms-of-service' | 'privacy-policy') => void;
  onUpdateUser: (updatedUser: User) => void;
  onProfileModalOpen: () => void;
  onClassManageModalOpen: () => void;
  classes: Class[];
  currentClass: Class | null;
  memberGroups?: string[];
  currentMemberGroup?: string;
  onMemberGroupChange?: (group: string) => void;
  onSeatAssignmentClick?: () => void;
  onLevelSettingsClick?: () => void;
  onPointManagementClick?: () => void;
  onItemCardSettingsClick?: () => void;
  onAssignmentSettingsClick?: () => void;
  onNewAlarmModalOpen: () => void;
  onSetNameModalOpen: () => void;
  onEditSetNameModalOpen: () => void;
  onAlarmPopupOpen: (alarmInfo?: {
    time: string;
    type: '1M' | '3M' | '5M';
    name?: string;
    minutesBefore?: number;
  }) => boolean | void;
  onAlarmPopupClose: () => void;
  timeFormat: '12h' | '24h';
  setTimeFormat: (format: '12h' | '24h') => void;
  onCloneSetSave?: (newName: string) => void;
  // 전역 알람 서비스 props 추가
  alarmSets: AlarmSet[];
  setAlarmSets: React.Dispatch<React.SetStateAction<AlarmSet[]>>;
  loadUserAlarmData: () => Promise<void>;
  themeMode: 'dark' | 'light';
  onToggleTheme: () => void;
  onLicenseClick?: () => void;
}



const PageContainer = styled.div`
  min-height: 100vh;
  background: var(--color-bg);
  display: flex;
  flex-direction: column;
  overflow: hidden; /* 스크롤 숨김 */
  padding-top: 36px; /* 상단 헤더 공간 확보 */
  padding-bottom: 80px; /* 하단 네비바 공간 확보 */

  @keyframes pulse {
    0% {
      transform: scale(1);
      box-shadow: 0 4px 12px rgba(var(--color-accent-rgb), 0.3);
    }
    50% {
      transform: scale(1.05);
      box-shadow: 0 6px 16px rgba(var(--color-accent-rgb), 0.5);
    }
    100% {
      transform: scale(1);
      box-shadow: 0 4px 12px rgba(var(--color-accent-rgb), 0.3);
    }
  }
`;



const AlarmSetHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4px 16px 8px 16px; /* 상단 패딩을 8px→16px로 증가 */
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: fixed !important; /* CSS 변수 사용 */
  top: 36px !important; /* var(--header-height) 대신 직접 값 사용 */
  left: 0 !important;
  right: 0 !important;
  z-index: 99 !important; /* 파란색 헤더보다 낮은 z-index */
  min-height: 128px; /* height를 min-height로 변경하여 동적 조정 가능 */
  box-sizing: border-box;
  
  @media (max-width: 600px) {
    padding: 14px 12px 20px 12px; /* 상단 패딩을 6px→14px로 증가 */
    min-height: 108px; /* height를 min-height로 변경 */
  }
  
  @media (max-width: 480px) {
    padding: 12px 8px 16px 8px; /* 상단 패딩을 4px→12px로 증가 */
    min-height: 98px; /* height를 min-height로 변경 */
  }
`;



const SetControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  position: relative;
  flex-wrap: wrap; /* 내용이 많을 때 줄바꿈 */
  gap: 8px; /* 요소들 사이 간격 */
  
  @media (max-width: 600px) {
    gap: 6px;
  }
  
  @media (max-width: 480px) {
    gap: 4px;
  }
`;

const ArrowButton = styled.button<{ disabled: boolean }>`
  background: ${props => props.disabled ? 'rgba(255, 255, 255, 0.08)' : 'var(--color-panel)'};
  color: ${props => props.disabled ? 'var(--color-text-muted)' : 'var(--color-text-primary)'};
  border: 1px solid var(--color-border);
  border-radius: 8px;
  width: 36px;
  min-width: 32px;
  height: 36px;
  min-height: 32px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  position: absolute;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    background: ${props => props.disabled ? 'rgba(255, 255, 255, 0.08)' : 'var(--color-border)'};
    border-color: var(--color-border);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const SetName = styled.span`
  font-size: 1.8rem; /* 2.4rem→1.8rem로 감소 */
  color: var(--color-text-primary);
  font-weight: bold;
  min-width: 120px;
  text-align: center;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: rgba(var(--color-accent-rgb), 0.1);
  }
  
  @media (max-width: 600px) {
    font-size: 1.5rem;
    min-width: 100px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
    min-width: 80px;
  }
`;

const ActivateButton = styled.div<{ $isActive: boolean; $isDisabled?: boolean }>`
  display: flex;
  background: ${props => props.$isDisabled ? 'rgba(var(--color-accent-rgb), 0.3)' : 'var(--color-accent-strong)'};
  border-radius: 13px;
  padding: 2px;
  position: relative;
  cursor: ${props => props.$isDisabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  height: 26px;
  min-width: 120px;
  width: auto;
  margin-top: 2px;
  
  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.$isActive ? '50%' : '2px'};
    width: calc(50% - 2px);
    height: calc(100% - 4px);
    background: ${props => props.$isDisabled ? 'rgba(255, 255, 255, 0.5)' : 'var(--color-surface)'};
    border-radius: 11px;
    transition: left 0.3s ease;
    z-index: 1;
  }
`;

const ActivateToggleOption = styled.div<{ $isActive: boolean; $isDisabled?: boolean }>`
  flex: 1;
  padding: 2px 12px;
  font-size: 11px;
  font-weight: 600;
  color: ${props => {
    if (props.$isDisabled) {
      return props.$isActive ? 'rgba(var(--color-accent-rgb), 0.4)' : 'rgba(255, 255, 255, 0.6)';
    }
    return props.$isActive ? 'var(--color-toggle-active)' : 'var(--color-toggle-inactive)';
  }};
  text-align: center;
  position: relative;
  z-index: 2;
  transition: color 0.3s ease;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  
  @media (max-width: 600px) {
    padding: 2px 10px;
    font-size: 10px;
  }
  
  @media (max-width: 480px) {
    padding: 2px 8px;
    font-size: 9px;
  }
`;

const SetInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 120px;
  position: relative;
  margin: 0 auto;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 12px;
  
  @media (max-width: 600px) {
    gap: 4px;
    margin-top: 8px;
  }
  
  @media (max-width: 480px) {
    gap: 2px;
    margin-top: 6px;
  }
`;


const AddSetButton = styled.button`
  padding: 2px 8px;
  background: var(--color-surface);
  color: var(--color-dark-gray-text);
  border: 1px solid var(--color-dark-gray-text);
  border-radius: 8px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  min-height: 16px;
  margin: 0 4px;
  
  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-secondary);
    color: var(--color-text-secondary);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(108, 117, 125, 0.3);
  }
  
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(108, 117, 125, 0.3);
  }
`;

const CloneSetButton = styled.button`
  padding: 2px 8px;
  background: var(--color-surface);
  color: var(--color-dark-gray-text);
  border: 1px solid var(--color-dark-gray-text);
  border-radius: 8px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  min-height: 16px;
  margin: 0 4px;
  
  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-secondary);
    color: var(--color-text-secondary);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(108, 117, 125, 0.3);
  }
  
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(108, 117, 125, 0.3);
  }
`;

const DeleteSetButton = styled.button`
  padding: 2px 8px;
  background: var(--color-surface);
  color: var(--color-dark-gray-text);
  border: 1px solid var(--color-dark-gray-text);
  border-radius: 8px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  min-height: 16px;
  margin: 0 4px;
  
  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-secondary);
    color: var(--color-text-secondary);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(108, 117, 125, 0.3);
  }
  
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(108, 117, 125, 0.3);
  }
`;

const Content = styled.main<{ $headerHeight: number }>`
  flex: 1;
  max-width: 600px;
  margin: 0 auto;
  width: 100%;
  padding: 20px;
  padding-top: calc(44px + ${props => props.$headerHeight}px + 24px); /* 헤더(44px) + 동적 높이 + 여유 공간(24px) */
  overflow-y: auto; /* 세로 스크롤만 허용 */
  height: calc(100vh - 44px - ${props => props.$headerHeight}px - 24px); /* 헤더(44px) + 동적 높이 + 여유 공간(24px) */
  ${hideScrollbar}
  
  @media (max-width: 600px) {
    padding-top: calc(44px + ${props => props.$headerHeight}px + 20px); /* 헤더(44px) + 동적 높이 + 여유 공간(20px) */
    height: calc(100vh - 44px - ${props => props.$headerHeight}px - 20px); /* 헤더(44px) + 동적 높이 + 여유 공간(20px) */
  }
  
  @media (max-width: 480px) {
    padding-top: calc(44px + ${props => props.$headerHeight}px + 16px); /* 헤더(44px) + 동적 높이 + 여유 공간(16px) */
    height: calc(100vh - 44px - ${props => props.$headerHeight}px - 16px); /* 헤더(44px) + 동적 높이 + 여유 공간(16px) */
  }
`;



const DaySelector = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  
  @media (max-width: 480px) {
    gap: 4px;
  }
  
  @media (max-width: 360px) {
    gap: 2px;
  }
`;

const DayButton = styled.button<{ $isSelected: boolean }>`
  width: 40px;
  height: 40px;
  min-width: 32px;
  min-height: 32px;
  border: 2px solid ${props => props.$isSelected ? 'transparent' : 'var(--color-border)'};
  border-radius: 50%;
  background: ${props => props.$isSelected ? 'var(--color-accent-strong)' : 'var(--color-panel)'};
  color: ${props => props.$isSelected ? 'white' : '#666'};
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: bold;
  transition: all 0.3s ease;
  box-sizing: border-box;
  
  /* 다크모드: 텍스트 색상 및 배경색 변경 */
  :root:not([data-theme='light']) & {
    color: ${props => props.$isSelected ? '#ffffff' : 'rgba(255, 255, 255, 0.5)'};
    /* 다크모드: 선택되지 않았을 때 연한 테두리, 선택되었을 때 흰색 테두리 */
    border: 2px solid ${props => props.$isSelected ? '#ffffff' : 'rgba(255, 255, 255, 0.45)'};
    /* 다크모드: 비선택일 때 연한 회색 배경 (선택했을 때 색상과 같은 색이지만 연하게) */
    background: ${props => props.$isSelected ? 'var(--color-accent-strong)' : 'var(--color-accent-soft)'};
  }
  
  @media (max-width: 480px) {
    width: 38px;
    height: 38px;
    min-width: 32px;
    min-height: 32px;
    font-size: 0.8rem;
  }
  
  @media (max-width: 360px) {
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;
    font-size: 0.75rem;
  }
  
  &:hover {
    background: ${props => props.$isSelected ? 'var(--color-accent)' : 'var(--color-border)'};
  }
  
  &:focus {
    outline: none;
  }
`;

const SelectedDayInfo = styled.div`
  text-align: center;
  color: var(--color-text-primary);
  margin-bottom: 20px;
  font-size: 1.1rem;
  font-weight: bold;
`;

const AlarmsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const EmptyState = styled.div`
  text-align: center;
  color: var(--color-text-primary);
  padding: 40px 20px;
  background: var(--color-panel);
  border-radius: 12px;
  margin-top: 20px;
  border: 1px solid var(--color-border);
`;

const EmptyStateTitle = styled.h3`
  margin-bottom: 10px;
  font-size: 1.2rem;
  color: var(--color-text-primary);
`;

const EmptyStateText = styled.p`
  margin: 0;
  color: #666;
  
  /* 다크모드: 조금 더 밝게 */
  :root:not([data-theme='light']) & {
    color: #777777;
  }
`;

const TimeFormatSelector = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 20px;
`;

const TimeFormatButton = styled.button<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  background: var(--color-surface);
  color: ${props => props.$isSelected ? 'var(--color-text-primary)' : 'var(--color-text-muted)'};
  border: 1px solid var(--color-border);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 80px;
  min-height: 36px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  font-size: 14px;
  font-weight: ${props => props.$isSelected ? '600' : '400'};
  
  &:hover {
    background-color: var(--color-panel);
    border-color: var(--color-text-muted);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  @media (max-width: 420px) {
    padding: 6px 12px;
    width: 70px;
    min-height: 32px;
  }
  
  @media (max-width: 480px) {
    padding: 6px 12px;
    width: 70px;
    min-height: 32px;
  }
`;

const CopyDayButtonStyled = styled.button`
  width: 120px;
  height: 40px;
  min-width: 80px;
  min-height: 36px;
  border: 2px solid var(--color-border);
  border-radius: 20px;
  background: var(--color-panel);
  color: #666;
  cursor: pointer;
  font-size: 0.9rem;
  
  /* 다크모드: 조금 더 밝게 */
  :root:not([data-theme='light']) & {
    color: #777777;
  }
  font-weight: bold;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 480px) {
    width: 100px;
    height: 36px;
    font-size: 0.8rem;
  }
  
  @media (max-width: 360px) {
    width: 80px;
    height: 32px;
    font-size: 0.75rem;
  }
  
  &:hover {
    background: var(--color-border);
  }
  
  &:focus {
    outline: none;
  }
`;

const BottomButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 16px;
`;



const DeleteAllAlarmsButton = styled.button`
  width: 120px;
  height: 40px;
  min-width: 80px;
  min-height: 36px;
  border: 2px solid var(--color-border);
  border-radius: 20px;
  background: var(--color-panel);
  color: #666;
  cursor: pointer;
  font-size: 0.9rem;
  
  /* 다크모드: 조금 더 밝게 */
  :root:not([data-theme='light']) & {
    color: #777777;
  }
  font-weight: bold;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 480px) {
    width: 100px;
    height: 36px;
    font-size: 0.8rem;
  }
  
  @media (max-width: 360px) {
    width: 80px;
    height: 32px;
    font-size: 0.75rem;
  }
  
  &:hover {
    background: var(--color-border);
  }
  
  &:focus {
    outline: none;
  }
`;

const FloatingActionButton = styled.button`
  position: fixed;
  bottom: calc(env(safe-area-inset-bottom, 0px) + 60px);
  right: 24px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: var(--color-accent-strong);
  color: white;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(var(--color-accent-rgb), 0.3);
  transition: all 0.3s ease;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: var(--color-accent);
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(var(--color-accent-rgb), 0.4);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    bottom: calc(env(safe-area-inset-bottom, 0px) + 60px);
    right: 20px;
    font-size: 18px;
  }
`;

const PlusIcon = styled.span`
  font-size: 18px;
  font-weight: bold;
  line-height: 1;
`;

// 복사 완료 메시지 팝업 스타일
const CopySuccessOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CopySuccessContainer = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 24px;
  max-width: 320px;
  width: 90%;
  border: var(--modal-border);
  box-shadow: var(--modal-shadow-large);
  text-align: center;
`;

const CopySuccessContent = styled.div`
  margin-bottom: 20px;
`;

const CopySuccessIcon = styled.div`
  width: 48px;
  height: 48px;
  background: var(--color-accent-strong);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: white;
  margin: 0 auto 16px;
  font-weight: bold;
`;

const CopySuccessTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--color-text-primary);
  line-height: 1.4;
`;

const CopySuccessFooter = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
`;

const CloseAfterCopyButton = styled.button`
  background: var(--color-accent-strong);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 24px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  margin: 0 auto;
  display: block;
  
  &:hover {
    background: var(--color-accent);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const AlarmPageBottomLogo = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 100px;
  padding-bottom: 24px;
  padding-left: 20px;
  padding-right: 20px;
  margin-top: 0;

  img {
    height: 48px;
    width: auto;
    object-fit: contain;
    opacity: 0.12;
    filter: brightness(0) saturate(0);
  }

  :root:not([data-theme='light']) & img {
    opacity: 0.1;
  }
`;

const AlarmPage: React.FC<AlarmPageProps> = ({ 
  user, 
  onLogout, 
  onNavigate,
  onUpdateUser,
  onProfileModalOpen,
  onClassManageModalOpen,
  classes,
  currentClass,
  memberGroups,
  currentMemberGroup,
  onMemberGroupChange,
  onSeatAssignmentClick,
  onLevelSettingsClick,
  onPointManagementClick,
  onItemCardSettingsClick,
  onAssignmentSettingsClick,
  onNewAlarmModalOpen, 
  onSetNameModalOpen, 
  onEditSetNameModalOpen, 
  timeFormat,
  setTimeFormat,
  // 전역 알람 서비스 props 받기
  alarmSets,
  setAlarmSets,
  loadUserAlarmData,
  themeMode,
  onToggleTheme,
  onLicenseClick
}) => {

  const [selectedDay, setSelectedDay] = useState(() => {
    // JavaScript의 getDay()는 일요일=0, 월요일=1, ..., 토요일=6
    // getDayName 함수와 일치하도록 그대로 사용
    return new Date().getDay(); // 0=일요일, 1=월요일, ..., 6=토요일
  });
  const [currentSetIndex, setCurrentSetIndex] = useState(0);
  const [currentClassId, setCurrentClassId] = useState<string | null>(null);
  const [newlyCreatedSetId, setNewlyCreatedSetId] = useState<string | null>(null);
  const [shouldMoveToNewSet, setShouldMoveToNewSet] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true); // 페이지 초기 로드 여부
  const lastLoadedClassIdRef = useRef<string | null>(null); // 중복 로드 방지
  
  // 모달 상태들
  const [showNewAlarmModal, setShowNewAlarmModal] = useState(false);
  const [showSetNameModal, setShowSetNameModal] = useState(false);
  const [showCloneSetModal, setShowCloneSetModal] = useState(false);
  const [showEditSetNameModal, setShowEditSetNameModal] = useState(false);
  const [showCopyDayModal, setShowCopyDayModal] = useState(false);
  const [copySourceDay, setCopySourceDay] = useState<number>(0);
  
  // 복사 완료 메시지 팝업 상태
  const [isCopySuccessModalOpen, setIsCopySuccessModalOpen] = useState(false);
  const [copySuccessMessage, setCopySuccessMessage] = useState('');
  const [isDuplicateMessage, setIsDuplicateMessage] = useState(false);
  
  // 단일 확장 알람 ID 관리
  const [expandedAlarmId, setExpandedAlarmId] = useState<string | null>(null);
  const [headerHeight, setHeaderHeight] = useState(128); // 기본값 128px
  const headerRef = useRef<HTMLDivElement>(null);
  





  // 현재 클래스 ID 로드 및 클래스 변경 감지
  useEffect(() => {
    console.log('AlarmPage: 클래스 ID 로드 시작');
    const classId = loadCurrentClassId(user.id);
    console.log('AlarmPage: 로드된 클래스 ID:', classId);
    
    if (classId) {
      // 현재 클래스 ID 사용 (user.currentClassId 우선)
      const currentId = user.currentClassId || classId;
      console.log('AlarmPage: 현재 클래스 ID:', currentId);
      setCurrentClassId(currentId);
    } else if (user.currentClassId) {
      console.log('AlarmPage: user.currentClassId 사용:', user.currentClassId);
      setCurrentClassId(user.currentClassId);
    }
  }, [user.id, user.currentClassId]); // user.currentClassId 변경될 때도 클래스 ID 로드

  // 알람세트를 생성일자 순으로 정렬하는 함수
  const sortAlarmSetsByCreationDate = (alarmSets: AlarmSet[]): AlarmSet[] => {
    return alarmSets.sort((a, b) => {
      // 기본 알람세트는 항상 첫 번째 (인덱스 0)
      if (a.id === 'default-alarm-set') return -1;
      if (b.id === 'default-alarm-set') return 1;
      
      // createdAt이 없는 경우 id에서 timestamp 추출 시도
      const aTimestamp = a.createdAt || parseInt(a.id) || 0;
      const bTimestamp = b.createdAt || parseInt(b.id) || 0;
      
      // 오름차순 정렬 (오래된 것부터, 오른쪽 화살표로 최신 접근)
      return aTimestamp - bTimestamp;
    }).map(set => ({
      ...set,
      createdAt: set.createdAt || parseInt(set.id) || 0 // createdAt이 없으면 id에서 추출
    }));
  };

  // 앱 시작 시 오디오 파일 미리 로드
  useEffect(() => {
    const preloadSounds = async () => {
      try {
        console.log('알람 소리 파일들을 미리 로드합니다...');
        await preloadAlarmSounds();
        console.log('모든 알람 소리 파일이 성공적으로 로드되었습니다.');
      } catch (error) {
        console.warn('알람 소리 파일 미리 로드 중 오류 발생:', error);
      }
    };
    
    preloadSounds();
  }, []); // 컴포넌트 마운트 시 한 번만 실행

  // 헤더 높이 측정 및 동적 조정
  useEffect(() => {
    const updateHeaderHeight = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        setHeaderHeight(height);
      }
    };

    // 초기 측정
    updateHeaderHeight();

    // 리사이즈 이벤트 리스너 추가
    const resizeObserver = new ResizeObserver(updateHeaderHeight);
    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }

    // 클린업
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // 추가: user 객체가 변경될 때마다 currentClassId를 강제로 다시 로드
  useEffect(() => {
    console.log('AlarmPage: user 객체 변경 감지, currentClassId 강제 재로드');
    
    if (user.currentClassId && user.currentClassId !== currentClassId) {
      console.log('AlarmPage: user.currentClassId로 업데이트:', user.currentClassId);
      setCurrentClassId(user.currentClassId);
    }
  }, [user, currentClassId]); // user 객체와 currentClassId가 변경될 때마다 실행

  // 클래스별 알람 데이터 로드
  useEffect(() => {
    if (!currentClassId) {
      return;
    }

    // 동일 클래스에 대해 중복 로드 방지
    if (lastLoadedClassIdRef.current === currentClassId) {
      return;
    }
    lastLoadedClassIdRef.current = currentClassId;

    console.log(`클래스 변경 감지: ${currentClassId}로 알람 데이터 로드 시작`);

    // 클래스 변경 시 초기 로드 상태만 설정 (빈 배열로 초기화하지 않음 - 깜박임 방지)
    setIsInitialLoad(true);
    setSelectedDay(new Date().getDay()); // 0=일요일, 1=월요일, ..., 6=토요일

    // 전역 알람 서비스에서 데이터 로드
    loadUserAlarmData();
  }, [currentClassId, loadUserAlarmData]);

  // 알람 세트 선택 로직 (우선순위: 새 생성 > 초기 로드 시 활성화된 세트 > 현재 세트 유지)
  useEffect(() => {
    console.log('🔍 useEffect([alarmSets]) 실행:', {
      alarmSetsLength: alarmSets.length,
      currentSetIndex,
      newlyCreatedSetId,
      shouldMoveToNewSet,
      isInitialLoad,
      alarmSets: alarmSets.map(set => ({ name: set.name, id: set.id, isActive: set.isActive }))
    });
    
    console.log('🔍 상태 체크:', {
      'newlyCreatedSetId 타입': typeof newlyCreatedSetId,
      'newlyCreatedSetId 값': newlyCreatedSetId,
      'shouldMoveToNewSet 타입': typeof shouldMoveToNewSet,
      'shouldMoveToNewSet 값': shouldMoveToNewSet,
      '조건 결과': !!(newlyCreatedSetId || shouldMoveToNewSet)
    });
    
    if (alarmSets.length > 0) {
      // 알람 세트 정렬 확인 및 적용 (정렬이 필요한 경우에만 업데이트하여 불필요한 리렌더링 방지)
      const sortedAlarmSets = sortAlarmSetsByCreationDate([...alarmSets]);
      // 정렬이 실제로 변경되었는지 확인 (참조 비교로 성능 개선)
      const needsSorting = sortedAlarmSets.some((set, index) => set.id !== alarmSets[index]?.id);
      if (needsSorting) {
        console.log('🔄 알람 세트 정렬 적용:', sortedAlarmSets.map(set => ({ name: set.name, id: set.id })));
        setAlarmSets(sortedAlarmSets);
        return; // 정렬이 적용되면 다음 useEffect에서 처리
      }
      
      // 우선순위 1: 새로 생성된 세트가 있으면 해당 세트로 이동
      if (newlyCreatedSetId || shouldMoveToNewSet) {
        console.log('🎯 새로 생성된 세트 처리:', { newlyCreatedSetId, shouldMoveToNewSet });
        
        if (newlyCreatedSetId) {
          const newSetIndex = sortedAlarmSets.findIndex(set => set.id === newlyCreatedSetId);
          if (newSetIndex !== -1) {
            console.log(`🔄 새로 생성된 알람 세트로 이동: ${sortedAlarmSets[newSetIndex].name} (인덱스: ${newSetIndex})`);
            setCurrentSetIndex(newSetIndex);
            setNewlyCreatedSetId(null);
            setShouldMoveToNewSet(false);
            setIsInitialLoad(false); // 초기 로드 완료
            return;
          }
        }
        
        // 마지막 인덱스로 이동 (백업 방법)
        const lastIndex = sortedAlarmSets.length - 1;
        if (lastIndex >= 0) {
          console.log(`🔄 마지막 인덱스로 이동: ${sortedAlarmSets[lastIndex].name} (인덱스: ${lastIndex})`);
          setCurrentSetIndex(lastIndex);
        }
        
        setNewlyCreatedSetId(null);
        setShouldMoveToNewSet(false);
        setIsInitialLoad(false); // 초기 로드 완료
        return;
      }
      
      // 우선순위 2: 초기 로드 시 활성화된 세트로 이동
      if (isInitialLoad) {
        console.log('🏠 페이지 초기 로드 - 활성화된 세트 찾기');
        const activeSetIndex = sortedAlarmSets.findIndex(set => set.isActive);
        if (activeSetIndex !== -1) {
          console.log(`🔄 활성화된 알람세트로 이동: ${sortedAlarmSets[activeSetIndex].name} (인덱스: ${activeSetIndex})`);
          setCurrentSetIndex(activeSetIndex);
        } else {
          console.log('🔄 활성화된 알람세트가 없어 첫 번째 세트 선택');
          setCurrentSetIndex(0);
        }
        setIsInitialLoad(false); // 초기 로드 완료
        return;
      }
      
      // 우선순위 3: 현재 선택된 세트가 유효하지 않으면 수정
      const currentSet = alarmSets[currentSetIndex];
      const isValidCurrentSet = currentSet && currentSetIndex < alarmSets.length;
      
      if (!isValidCurrentSet) {
        console.log('❌ 현재 선택된 세트가 유효하지 않음 - 첫 번째 세트로 이동');
        setCurrentSetIndex(0);
      } else {
        console.log(`✅ 현재 선택된 세트 유지: ${currentSet.name} (인덱스: ${currentSetIndex})`);
      }
    }
    // 프리 플랜에서는 항상 첫 번째 세트만 사용 가능
    if (user.userType === 'free' && currentSetIndex !== 0) {
      console.log('🔄 프리 플랜 사용자 - 첫 번째 세트로 자동 이동');
      setCurrentSetIndex(0);
    }
  }, [alarmSets, newlyCreatedSetId, shouldMoveToNewSet, currentSetIndex, isInitialLoad, setAlarmSets, user.userType]);



  // 클래스별 알람 세트 데이터 저장
  useEffect(() => {
    if (alarmSets.length > 0 && currentClassId) {
      const saveUserAlarmData = async () => {
        try {
          // Firestore에 클래스별 알람 데이터 저장 시도
          await userDataService.saveClassAlarmSets(user.id, currentClassId, alarmSets);
        } catch (error) {
          console.error('Firestore에 클래스별 알람 데이터 저장 실패:', error);
          
          // Firebase 실패 시 로컬 스토리지에 저장
          try {
            saveClassAlarmSets(alarmSets, user.id, currentClassId);
          } catch (localError) {
            console.error('로컬 스토리지에도 저장 실패:', localError);
          }
        }
      };
      
      saveUserAlarmData();
    }
  }, [alarmSets, user.id, currentClassId]);

  const addAlarm = () => {
    // 클래스 ID가 없으면 알람 생성 불가
    if (!currentClassId) {
      alert('클래스를 먼저 선택해주세요.');
      return;
    }
    setShowNewAlarmModal(true);
  };

  const handleCreateNewAlarm = async (newAlarmData: { time: string; type: '1M' | '3M' | '5M'; name: string; category?: 'basic' | 'class' }) => {
    // 클래스 ID가 없으면 알람 생성 불가
    if (!currentClassId) {
      alert('클래스를 먼저 선택해주세요.');
      return;
    }
    
    // 중복 체크: 같은 요일에 같은 시간의 알람이 있는지 확인
    const isDuplicate = currentAlarms.some((existingAlarm: Alarm) => 
      existingAlarm.time === newAlarmData.time && 
      existingAlarm.days.includes(selectedDay)
    );
    
    if (isDuplicate) {
      alert(`${getDayName(selectedDay)}요일에 같은 시간(${newAlarmData.time})의 알람이 이미 존재합니다.`);
      return;
    }
    
    const newAlarm: Alarm = {
      id: Date.now().toString(),
      time: newAlarmData.time,
      type: newAlarmData.type,
      name: newAlarmData.name,
      days: [selectedDay],
      isActive: true, // 기본적으로 활성화
      isExpanded: false,
      category: newAlarmData.category || 'basic', // 기본값: 'basic'
    };
    
    // 현재 알람 세트에 새 알람 추가
    if (alarmSets.length > 0 && currentSetIndex < alarmSets.length) {
      const updatedAlarms = sortAlarmsByTime([...currentAlarms, newAlarm]);
      const updatedAlarmSets = alarmSets.map((set, index) => 
        index === currentSetIndex 
          ? { ...set, alarms: updatedAlarms }
          : set
      );
      
      setAlarmSets(updatedAlarmSets);
      
      // 알람 생성 후 즉시 Firestore에 저장
      const saveNewAlarm = async () => {
        try {
          console.log('🔔 새 알람 생성 후 저장 시작:', { userId: user.id, classId: currentClassId, alarmCount: updatedAlarmSets.length });
          await userDataService.saveClassAlarmSets(user.id, currentClassId, updatedAlarmSets);
          console.log('✅ 새 알람 저장 완료');
        } catch (error) {
          console.error('❌ 알람 생성 후 저장 실패:', error);
          // Firestore 실패 시 로컬 스토리지에 저장
          import('../utils/storage').then(({ saveClassAlarmSets }) => {
            saveClassAlarmSets(updatedAlarmSets, user.id, currentClassId);
          });
        }
      };
      await saveNewAlarm();
    }
    
    setShowNewAlarmModal(false);
  };

  const handleCancelNewAlarm = () => {
    setShowNewAlarmModal(false);
  };



  const [showLicenseLimitModal, setShowLicenseLimitModal] = useState(false);
  const [licenseLimitMessage, setLicenseLimitMessage] = useState('');

  const handleCreateNewAlarmSet = () => {
    // 클래스 ID가 없으면 알람세트 생성 불가
    if (!currentClassId) {
      alert('클래스를 먼저 선택해주세요.');
      return;
    }
    
    // 알람 세트 수 제한 체크
    const limitCheck = checkAlarmSetLimit(user, alarmSets.length);
    if (!limitCheck.allowed) {
      setLicenseLimitMessage(limitCheck.message || '');
      setShowLicenseLimitModal(true);
      return;
    }
    
    setShowSetNameModal(true);
  };

  const handleCloneAlarmSet = () => {
    // 클래스 ID가 없으면 알람세트 복제 불가
    if (!currentClassId) {
      alert('클래스를 먼저 선택해주세요.');
      return;
    }
    
    if (alarmSets.length === 0 || currentSetIndex >= alarmSets.length) {
      alert('복제할 알람 세트가 없습니다.');
      return;
    }
    
    // 복제 모달을 열기
    setShowCloneSetModal(true);
  };

  const handleEditSetName = () => {
    // 클래스 ID가 없으면 알람세트 수정 불가
    if (!currentClassId) {
      alert('클래스를 먼저 선택해주세요.');
      return;
    }
    
    if (alarmSets.length === 0 || currentSetIndex >= alarmSets.length) {
      alert('수정할 알람 세트가 없습니다.');
      return;
    }
    setShowEditSetNameModal(true);
  };

  const handleDeleteAlarmSet = () => {
    // 클래스 ID가 없으면 알람세트 삭제 불가
    if (!currentClassId) {
      alert('클래스를 먼저 선택해주세요.');
      return;
    }
    
    if (alarmSets.length === 0 || currentSetIndex >= alarmSets.length) {
      alert('삭제할 알람 세트가 없습니다.');
      return;
    }
    
    const currentSet = alarmSets[currentSetIndex];
    
    // 기본 알람 세트는 삭제할 수 없음
    if (currentSet.id === 'default-alarm-set') {
      alert('기본 알람 세트는 삭제할 수 없습니다.');
      return;
    }
    
    const confirmDelete = window.confirm(`"${currentSet.name}" 알람 세트를 삭제하시겠습니까?`);
    
    if (confirmDelete) {
      const updatedAlarmSets = alarmSets.filter((_, index) => index !== currentSetIndex);
      setAlarmSets(updatedAlarmSets);
      
      // 삭제된 세트가 마지막 세트였다면 이전 세트로 이동
      if (currentSetIndex >= alarmSets.length - 1 && currentSetIndex > 0) {
        setCurrentSetIndex(currentSetIndex - 1);
      }
      
      // 삭제 후 즉시 Firebase에 저장
      const saveDeletedAlarmSet = async () => {
        try {
          await userDataService.saveClassAlarmSets(user.id, currentClassId, updatedAlarmSets);
        } catch (error) {
          console.error('알람세트 삭제 후 Firebase 저장 실패:', error);
          // Firebase 실패 시 로컬 스토리지에 저장
          import('../utils/storage').then(({ saveClassAlarmSets }) => {
            saveClassAlarmSets(updatedAlarmSets, user.id, currentClassId);
          });
        }
      };
      saveDeletedAlarmSet();
    }
  };

  const handleSaveAlarmSet = async (setName: string) => {
    console.log('🚀 handleSaveAlarmSet 함수 호출됨:', { setName, currentClassId });
    
    // 클래스 ID가 없으면 알람세트 저장 불가
    if (!currentClassId) {
      alert('클래스를 먼저 선택해주세요.');
      return;
    }
    
    // 중복 이름 체크
    const isDuplicateName = alarmSets.some(set => set.name === setName);
    if (isDuplicateName) {
      alert(`"${setName}" 이름의 알람 세트가 이미 존재합니다. 다른 이름을 사용해주세요.`);
      return;
    }
    
    const newSet: AlarmSet = {
      id: Date.now().toString(),
      name: setName,
      alarms: [], // 빈 알람 배열로 시작 (기존 알람 복사하지 않음)
      isActive: false, // 새로 생성된 세트는 비활성화 상태로 시작
      createdAt: Date.now() // 생성일자 추가
    };
    
    const updatedAlarmSets = sortAlarmSetsByCreationDate([...alarmSets, newSet]);
    
    console.log('📝 알람 세트 생성 중:', {
      newSetId: newSet.id,
      newSetName: newSet.name,
      updatedAlarmSetsLength: updatedAlarmSets.length,
      updatedAlarmSets: updatedAlarmSets.map(set => ({ name: set.name, id: set.id }))
    });
    
    // 먼저 새 세트 ID 설정
    console.log('📝 새 세트 상태 설정 시작:', { newSetId: newSet.id, setName });
    setNewlyCreatedSetId(newSet.id); // 새로 생성된 세트 ID 저장
    setShouldMoveToNewSet(true); // 새 세트로 이동해야 함을 표시
    console.log('📝 새 세트 상태 설정 완료');
    
    // 그 다음에 알람 세트 배열 업데이트 (useEffect에서 처리되도록)
    console.log('📝 알람 세트 배열 업데이트 시작');
    setAlarmSets(updatedAlarmSets);
    console.log('📝 알람 세트 배열 업데이트 완료');
    
    console.log('🔍 새로 생성된 세트 설정 완료:', { newSetId: newSet.id, setName });
    
    setShowSetNameModal(false);
    
    // 생성 후 즉시 Firestore에 저장 (비동기로 처리하여 상태 업데이트 방해 방지)
    const saveNewAlarmSet = async () => {
      try {
        console.log('📝 알람세트 생성 후 저장 시작:', { userId: user.id, classId: currentClassId, setName });
        await userDataService.saveClassAlarmSets(user.id, currentClassId, updatedAlarmSets);
        console.log('✅ 알람세트 생성 저장 완료');
      } catch (error) {
        console.error('❌ 알람세트 생성 후 Firestore 저장 실패:', error);
        // Firestore 실패 시 로컬 스토리지에 저장
        import('../utils/storage').then(({ saveClassAlarmSets }) => {
          saveClassAlarmSets(updatedAlarmSets, user.id, currentClassId);
        });
      }
    };
    // 비동기로 저장하되 await하지 않음 (상태 업데이트 방해 방지)
    saveNewAlarmSet();
  };

  const handleCancelSetName = () => {
    setShowSetNameModal(false); // 테스트용 - 로컬 상태 사용
  };

  const handleSaveEditSetName = async (newName: string) => {
    // 클래스 ID가 없으면 알람세트 수정 불가
    if (!currentClassId) {
      alert('클래스를 먼저 선택해주세요.');
      return;
    }
    
    const updatedAlarmSets = alarmSets.map((set, index) => 
      index === currentSetIndex 
        ? { ...set, name: newName }
        : set
    );
    
    setAlarmSets(updatedAlarmSets);
    
    // 수정 후 즉시 Firestore에 저장하고 모달 닫기
    const saveEditedAlarmSet = async () => {
      try {
        console.log('✏️ 알람세트 이름 수정 후 저장 시작:', { userId: user.id, classId: currentClassId, newName });
        await userDataService.saveClassAlarmSets(user.id, currentClassId, updatedAlarmSets);
        console.log('✅ 알람세트 이름 수정 저장 완료');
      } catch (error) {
        console.error('❌ 알람세트 이름 수정 후 저장 실패:', error);
        // Firestore 실패 시 로컬 스토리지에 저장
        import('../utils/storage').then(({ saveClassAlarmSets }) => {
          saveClassAlarmSets(updatedAlarmSets, user.id, currentClassId);
        });
      } finally {
        // 저장 완료 후 모달 닫기
        setShowEditSetNameModal(false);
      }
    };
    await saveEditedAlarmSet();
  };

  const handleCancelEditSetName = () => {
    setShowEditSetNameModal(false);
  };

  const handleSaveCloneSet = (newName: string) => {
    // 클래스 ID가 없으면 알람세트 복제 불가
    if (!currentClassId) {
      alert('클래스를 먼저 선택해주세요.');
      return;
    }
    
    // 알람 세트 수 제한 체크 (복제 시 1개 추가되므로 현재 개수로 체크)
    const limitCheck = checkAlarmSetLimit(user, alarmSets.length);
    if (!limitCheck.allowed) {
      setLicenseLimitMessage(limitCheck.message || '');
      setShowLicenseLimitModal(true);
      setShowCloneSetModal(false);
      return;
    }
    
    // 중복 이름 체크
    const isDuplicateName = alarmSets.some(set => set.name === newName);
    if (isDuplicateName) {
      alert(`"${newName}" 이름의 알람 세트가 이미 존재합니다. 다른 이름을 사용해주세요.`);
      return;
    }
    
    console.log(`알람세트 복제 시작: 클래스 ${currentClassId}, 이름: ${newName}`);
    console.log('현재 알람세트 상태:', alarmSets);
    console.log('현재 선택된 알람세트 인덱스:', currentSetIndex);
    
    if (alarmSets.length > 0 && currentSetIndex < alarmSets.length) {
      const originalSet = alarmSets[currentSetIndex];
      console.log('복제할 원본 알람세트:', originalSet);
      
      const clonedSet: AlarmSet = {
        id: Date.now().toString(),
        name: newName,
        alarms: [...originalSet.alarms], // 알람들을 깊은 복사
        isActive: false,
        createdAt: Date.now() // 복제일자 추가
      };
      
      const updatedAlarmSets = sortAlarmSetsByCreationDate([...alarmSets, clonedSet]);
      
      console.log('📝 알람 세트 복제 중:', {
        clonedSetId: clonedSet.id,
        clonedSetName: clonedSet.name,
        updatedAlarmSetsLength: updatedAlarmSets.length,
        updatedAlarmSets: updatedAlarmSets.map(set => ({ name: set.name, id: set.id }))
      });
      
      // 먼저 새 세트 ID 설정
      setNewlyCreatedSetId(clonedSet.id); // 새로 복제된 세트 ID 저장
      setShouldMoveToNewSet(true); // 새 세트로 이동해야 함을 표시
      
      // 그 다음에 알람 세트 배열 업데이트 (useEffect에서 처리되도록)
      setAlarmSets(updatedAlarmSets);
      
      console.log('🔍 새로 복제된 세트 설정 완료:', { clonedSetId: clonedSet.id, newName });
      
      // 복제 후 즉시 Firebase에 저장 (비동기로 처리하여 상태 업데이트 방해 방지)
      const saveClonedSet = async () => {
        try {
          await userDataService.saveClassAlarmSets(user.id, currentClassId, updatedAlarmSets);
        } catch (error) {
          console.error('알람세트 복제 후 Firebase 저장 실패:', error);
          // Firebase 실패 시 로컬 스토리지에 저장
          import('../utils/storage').then(({ saveClassAlarmSets }) => {
            saveClassAlarmSets(updatedAlarmSets, user.id, currentClassId);
          });
        }
      };
      // 비동기로 저장하되 await하지 않음 (상태 업데이트 방해 방지)
      saveClonedSet();
    }
    
    // 모달 닫기
    setShowCloneSetModal(false);
  };

  const handleCancelCloneSet = () => {
    setShowCloneSetModal(false);
  };

  // 요일 전체 알람 복사 함수
  const handleCopyDayAlarms = (sourceDay: number) => {
    setCopySourceDay(sourceDay);
    setShowCopyDayModal(true);
  };

  // 요일 전체 알람 복사 실행 (단일 요일용)
  const handleExecuteCopyDayAlarms = (targetDay: number) => {
    console.log('🔍 handleExecuteCopyDayAlarms 호출됨:', { targetDay, copySourceDay });
    
    if (copySourceDay === targetDay) {
      console.log('❌ 같은 요일이므로 무시');
      return; // 같은 요일은 무시
    }

    // 현재 알람 세트에서 소스 요일의 모든 알람을 가져옴
    const sourceDayAlarms = currentAlarms.filter(alarm => 
      alarm.days.includes(copySourceDay)
    );

    console.log('🔍 소스 요일 알람들:', { sourceDayAlarms, currentAlarms, copySourceDay });

    if (sourceDayAlarms.length === 0) {
      console.log('❌ 복사할 알람이 없음');
      return; // 복사할 알람이 없으면 무시
    }

    // 타겟 요일의 기존 알람들을 제거하고 소스 요일 알람들을 복사
    const updatedAlarms = currentAlarms
      .filter(alarm => !alarm.days.includes(targetDay)) // 타겟 요일 알람 제거
      .concat(sourceDayAlarms.map(alarm => ({
        ...alarm,
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9), // 새로운 ID 생성
        days: [targetDay], // 타겟 요일로 설정
        isExpanded: false
      })));

    // 알람 세트 업데이트
    if (alarmSets.length > 0 && currentSetIndex < alarmSets.length && currentClassId) {
      const updatedAlarmSets = alarmSets.map((set, index) => 
        index === currentSetIndex 
          ? { ...set, alarms: sortAlarmsByTime(updatedAlarms) }
          : set
      );
      
      setAlarmSets(updatedAlarmSets);
      
      // 복사 모달 먼저 닫기
      setShowCopyDayModal(false);
      
      // 복사 후 즉시 Firebase에 저장
      const saveCopiedAlarms = async () => {
        try {
          await userDataService.saveClassAlarmSets(user.id, currentClassId, updatedAlarmSets);
          
          // 복사 완료 팝업 표시
          const targetDayName = getDayName(targetDay);
          const sourceDayName = getDayName(copySourceDay);
          const message = `${sourceDayName}요일 알람이 ${targetDayName}요일로 복사되었습니다.`;
          console.log('🔍 복사 완료 메시지 설정:', message);
          setCopySuccessMessage(message);
          setIsDuplicateMessage(false);
          console.log('🔍 복사 완료 팝업 열기');
          setIsCopySuccessModalOpen(true);
        } catch (error) {
          console.error('요일 알람 복사 후 저장 실패:', error);
          // Firebase 실패 시 로컬 스토리지에 저장
          import('../utils/storage').then(({ saveClassAlarmSets }) => {
            saveClassAlarmSets(updatedAlarmSets, user.id, currentClassId);
          });
          
          // 저장 실패해도 복사는 완료되었으므로 팝업 표시
          const targetDayName = getDayName(targetDay);
          const sourceDayName = getDayName(copySourceDay);
          setCopySuccessMessage(`${sourceDayName}요일 알람이 ${targetDayName}요일로 복사되었습니다.`);
          setIsCopySuccessModalOpen(true);
        }
      };
      saveCopiedAlarms();
    }
  };

  // 요일 전체 알람 복사 실행 (복수 요일용)
  const handleExecuteCopyDayAlarmsMultiple = (targetDays: number[]) => {
    console.log('🔍 handleExecuteCopyDayAlarmsMultiple 호출됨:', { targetDays, copySourceDay });
    
    if (targetDays.length === 0) {
      console.log('❌ 타겟 요일이 없음');
      return;
    }

    // 현재 알람 세트에서 소스 요일의 모든 알람을 가져옴
    const sourceDayAlarms = currentAlarms.filter(alarm => 
      alarm.days.includes(copySourceDay)
    );

    if (sourceDayAlarms.length === 0) {
      return; // 복사할 알람이 없으면 무시
    }

    // 모든 타겟 요일의 기존 알람들을 제거하고 소스 요일 알람들을 복사
    let updatedAlarms = currentAlarms.filter(alarm => 
      !targetDays.some(targetDay => alarm.days.includes(targetDay))
    );

    // 각 타겟 요일에 대해 소스 알람들을 복사
    targetDays.forEach(targetDay => {
      if (targetDay !== copySourceDay) {
        const copiedAlarms = sourceDayAlarms.map(alarm => ({
          ...alarm,
          id: Date.now().toString() + Math.random().toString(36).substr(2, 9), // 새로운 ID 생성
          days: [targetDay], // 타겟 요일로 설정
          isExpanded: false
        }));
        updatedAlarms = updatedAlarms.concat(copiedAlarms);
      }
    });

    // 알람 세트 업데이트
    if (alarmSets.length > 0 && currentSetIndex < alarmSets.length && currentClassId) {
      const updatedAlarmSets = alarmSets.map((set, index) => 
        index === currentSetIndex 
          ? { ...set, alarms: sortAlarmsByTime(updatedAlarms) }
          : set
      );
      
      setAlarmSets(updatedAlarmSets);
      
      // 복사 모달 먼저 닫기
      setShowCopyDayModal(false);
      
      // 복사 후 즉시 Firebase에 저장
      const saveCopiedAlarms = async () => {
        try {
          await userDataService.saveClassAlarmSets(user.id, currentClassId, updatedAlarmSets);
          
          // 복사 완료 팝업 표시
          const targetDayNames = targetDays.map(day => getDayName(day)).join(', ');
          const sourceDayName = getDayName(copySourceDay);
          const message = `${sourceDayName}요일 알람이 ${targetDayNames}요일로 복사되었습니다.`;
          console.log('🔍 복사 완료 메시지 설정 (복수):', message);
          setCopySuccessMessage(message);
          setIsDuplicateMessage(false);
          console.log('🔍 복사 완료 팝업 열기 (복수)');
          setIsCopySuccessModalOpen(true);
        } catch (error) {
          console.error('요일 알람 복사 후 저장 실패:', error);
          // Firebase 실패 시 로컬 스토리지에 저장
          import('../utils/storage').then(({ saveClassAlarmSets }) => {
            saveClassAlarmSets(updatedAlarmSets, user.id, currentClassId);
          });
          
          // 저장 실패해도 복사는 완료되었으므로 팝업 표시
          const targetDayNames = targetDays.map(day => getDayName(day)).join(', ');
          const sourceDayName = getDayName(copySourceDay);
          setCopySuccessMessage(`${sourceDayName}요일 알람이 ${targetDayNames}요일로 복사되었습니다.`);
          setIsCopySuccessModalOpen(true);
        }
      };
      saveCopiedAlarms();
    }
  };

  const handleCancelCopyDayAlarms = () => {
    setShowCopyDayModal(false);
  };
  
  // 복사 완료 후 모달 닫기
  const handleCloseAfterCopy = () => {
    setIsCopySuccessModalOpen(false);
    setShowCopyDayModal(false);
  };
  
  // 복사 완료 팝업 상태 디버깅
  useEffect(() => {
    console.log('🔍 복사 완료 팝업 상태 변경:', isCopySuccessModalOpen);
    console.log('🔍 복사 완료 메시지:', copySuccessMessage);
  }, [isCopySuccessModalOpen, copySuccessMessage]);

  // 삭제 확인 모달 상태
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] = useState(false);
  const [deleteDayIndex, setDeleteDayIndex] = useState<number | null>(null);

  // 요일별 모든 알람 삭제 함수
  const handleDeleteAllAlarmsForDay = (dayIndex: number) => {
    setDeleteDayIndex(dayIndex);
    setIsDeleteConfirmModalOpen(true);
  };

  // 실제 삭제 실행 함수
  const executeDeleteAlarms = () => {
    if (deleteDayIndex === null) return;
    
    const dayIndex = deleteDayIndex;
    const dayName = getDayName(dayIndex);
    
    // 클래스 ID가 없으면 알람 삭제 불가
    if (!currentClassId) {
      alert('클래스를 먼저 선택해주세요.');
      setIsDeleteConfirmModalOpen(false);
      setDeleteDayIndex(null);
      return;
    }
    
    // 현재 알람 세트에서 해당 요일의 알람들을 제거
    if (alarmSets.length > 0 && currentSetIndex < alarmSets.length) {
      const updatedAlarms = currentAlarms.filter(alarm => 
        !alarm.days.includes(dayIndex)
      );
      
      const updatedAlarmSets = alarmSets.map((set, index) => 
        index === currentSetIndex 
          ? { ...set, alarms: updatedAlarms }
          : set
      );
      
      setAlarmSets(updatedAlarmSets);
      
      // 삭제 후 즉시 Firebase에 저장
      const saveDeletedAlarms = async () => {
        try {
          await userDataService.saveClassAlarmSets(user.id, currentClassId, updatedAlarmSets);
        } catch (error) {
          console.error('요일 알람 삭제 후 저장 실패:', error);
          // Firebase 실패 시 로컬 스토리지에 저장
          import('../utils/storage').then(({ saveClassAlarmSets }) => {
            saveClassAlarmSets(updatedAlarmSets, user.id, currentClassId);
          });
        }
      };
      saveDeletedAlarms();
      
      alert(`${dayName}요일 알람이 모두 삭제되었습니다.`);
    }
    
    setIsDeleteConfirmModalOpen(false);
    setDeleteDayIndex(null);
  };

  const handlePreviousSet = () => {
    if (currentSetIndex > 0) {
      // 프리 플랜에서는 첫 번째 세트만 사용 가능
      if (user.userType === 'free') {
        setLicenseLimitMessage('프리 플랜에서는 첫 번째 알람 세트만 사용할 수 있습니다. 이용권을 업그레이드하세요.');
        setShowLicenseLimitModal(true);
        return;
      }
      setCurrentSetIndex(prev => prev - 1);
    }
  };

  const handleNextSet = () => {
    if (currentSetIndex < alarmSets.length - 1) {
      // 프리 플랜에서는 첫 번째 세트만 사용 가능
      if (user.userType === 'free') {
        setLicenseLimitMessage('프리 플랜에서는 첫 번째 알람 세트만 사용할 수 있습니다. 이용권을 업그레이드하세요.');
        setShowLicenseLimitModal(true);
        return;
      }
      setCurrentSetIndex(prev => prev + 1);
    }
  };

  const loadCurrentSet = () => {
    // 현재 선택된 알람세트의 알람들은 currentAlarms로 자동으로 가져옴
    // 별도의 상태 업데이트가 필요 없음
  };

  const toggleAlarmSetActive = (setIndex: number) => {
    // 클래스 ID가 없으면 알람세트 활성화 불가
    if (!currentClassId) {
      alert('클래스를 먼저 선택해주세요.');
      return;
    }
    
    const updatedAlarmSets = alarmSets.map((set, index) => ({
      ...set,
      isActive: index === setIndex ? !set.isActive : false // 하나만 활성화
    }));
    
    setAlarmSets(updatedAlarmSets);
    
    // 활성화 상태 변경 후 즉시 Firebase에 저장
    const saveToggleAlarmSet = async () => {
      try {
        await userDataService.saveClassAlarmSets(user.id, currentClassId, updatedAlarmSets);
      } catch (error) {
        console.error('알람세트 활성화 상태 변경 후 저장 실패:', error);
        // Firebase 실패 시 로컬 스토리지에 저장
        import('../utils/storage').then(({ saveClassAlarmSets }) => {
          saveClassAlarmSets(updatedAlarmSets, user.id, currentClassId);
        });
      }
    };
    saveToggleAlarmSet();
  };

  // 현재 선택된 알람세트의 알람들을 로드
  useEffect(() => {
    loadCurrentSet();
  }, [currentSetIndex, alarmSets, currentClassId]); // currentClassId 추가

  // 활성화된 알람 세트가 변경될 때 현재 표시되는 알람들 업데이트
  // currentAlarms가 자동으로 업데이트되므로 별도의 상태 업데이트가 필요 없음

  const copyAlarm = (alarm: Alarm, targetDay: number) => {
    console.log('🔍 copyAlarm 호출됨:', { alarm, targetDay });
    
    // 중복 체크: 같은 시간, 타입, 요일에 이미 알람이 있는지 확인
    const isDuplicate = currentAlarms.some((existingAlarm: Alarm) => 
      existingAlarm.time === alarm.time && 
      existingAlarm.type === alarm.type && 
      existingAlarm.days.includes(targetDay)
    );

    if (!isDuplicate) {
      const copiedAlarm: Alarm = {
        ...alarm,
        id: Date.now().toString(),
        name: alarm.name || '',
        days: [targetDay],
        isExpanded: false,
      };
      
      // 현재 알람 세트에 복사된 알람 추가
      if (alarmSets.length > 0 && currentSetIndex < alarmSets.length) {
        const updatedAlarms = sortAlarmsByTime([...currentAlarms, copiedAlarm]);
        setAlarmSets(prev => 
          prev.map((set, index) => 
            index === currentSetIndex 
              ? { ...set, alarms: updatedAlarms }
              : set
          )
        );
        
        // 개별 알람 복사 완료 팝업 표시
        const targetDayName = getDayName(targetDay);
        const message = `알람이 ${targetDayName}요일로 복사되었습니다.`;
        console.log('🔍 개별 알람 복사 완료 메시지 설정:', message);
        setCopySuccessMessage(message);
        setIsDuplicateMessage(false);
        console.log('🔍 개별 알람 복사 완료 팝업 열기');
        setIsCopySuccessModalOpen(true);
      }
    } else {
      console.log('❌ 중복된 알람이 존재함');
      const targetDayName = getDayName(targetDay);
      const message = `${targetDayName}요일에 같은 시간의 알람이 이미 존재합니다.`;
      setCopySuccessMessage(message);
      setIsDuplicateMessage(true);
      setIsCopySuccessModalOpen(true);
    }
  };

  const handleDaySelect = (dayIndex: number) => {
    setSelectedDay(dayIndex);
    // 요일 변경 시 확장된 알람 블럭 닫기
    setExpandedAlarmId(null);
  };

  // getDayName 함수는 alarmUtils에서 import됨

  // 알람 확장/축소 관리
  const handleAlarmToggle = (alarmId: string) => {
    if (expandedAlarmId === alarmId) {
      // 같은 알람을 클릭하면 닫기
      setExpandedAlarmId(null);
    } else {
      // 다른 알람을 클릭하면 이전 알람은 닫고 새 알람 열기
      setExpandedAlarmId(alarmId);
    }
  };

  // 현재 선택된 알람세트의 알람들
  const currentAlarms = useMemo(
    () =>
      alarmSets.length > 0 && currentSetIndex < alarmSets.length
        ? alarmSets[currentSetIndex].alarms
        : [],
    [alarmSets, currentSetIndex]
  );
  
  // 디버깅: currentAlarms 변경 추적
  useEffect(() => {
    console.log('currentAlarms 변경됨:', {
      alarmSetsLength: alarmSets.length,
      currentSetIndex,
      currentAlarmsLength: currentAlarms.length,
      currentClassId
    });
  }, [currentAlarms, alarmSets.length, currentSetIndex, currentClassId]);
  
  // 선택된 요일의 알람만 필터링
  const filteredAlarms = currentAlarms.filter(alarm => alarm.days.includes(selectedDay));

  const updateAlarm = async (updatedAlarm: Alarm) => {
    // 클래스 ID가 없으면 알람 수정 불가
    if (!currentClassId) {
      alert('클래스를 먼저 선택해주세요.');
      return;
    }
    
    // 중복 체크: 같은 요일에 같은 시간의 알람이 있는지 확인 (자신 제외)
    const isDuplicate = currentAlarms.some((existingAlarm: Alarm) => 
      existingAlarm.id !== updatedAlarm.id && 
      existingAlarm.time === updatedAlarm.time && 
      existingAlarm.days.some((day: number) => updatedAlarm.days.includes(day))
    );
    
    if (isDuplicate) {
      const dayNames = updatedAlarm.days.map(day => getDayName(day)).join(', ');
      alert(`${dayNames}요일에 같은 시간(${updatedAlarm.time})의 알람이 이미 존재합니다.`);
      return;
    }
    
    // 현재 알람 세트의 알람 업데이트
    if (alarmSets.length > 0 && currentSetIndex < alarmSets.length) {
      const updatedAlarms = sortAlarmsByTime(
        currentAlarms.map((alarm: Alarm) => 
          alarm.id === updatedAlarm.id ? updatedAlarm : alarm
        )
      );
      const updatedAlarmSets = alarmSets.map((set, index) => 
        index === currentSetIndex 
          ? { ...set, alarms: updatedAlarms }
          : set
      );
      
      setAlarmSets(updatedAlarmSets);
      
      // 알람 수정 후 즉시 Firestore에 저장
      const saveUpdatedAlarm = async () => {
        try {
          console.log('✏️ 알람 수정 후 저장 시작:', { userId: user.id, classId: currentClassId, alarmId: updatedAlarm.id });
          await userDataService.saveClassAlarmSets(user.id, currentClassId, updatedAlarmSets);
          console.log('✅ 알람 수정 저장 완료');
        } catch (error) {
          console.error('❌ 알람 수정 후 저장 실패:', error);
          // Firestore 실패 시 로컬 스토리지에 저장
          import('../utils/storage').then(({ saveClassAlarmSets }) => {
            saveClassAlarmSets(updatedAlarmSets, user.id, currentClassId);
          });
        }
      };
      await saveUpdatedAlarm();
    }
  };

  const toggleAlarmActive = async (id: string) => {
    // 클래스 ID가 없으면 알람 활성화 불가
    if (!currentClassId) {
      alert('클래스를 먼저 선택해주세요.');
      return;
    }
    
    // 현재 알람 세트의 알람 활성화 상태 토글
    if (alarmSets.length > 0 && currentSetIndex < alarmSets.length) {
      const updatedAlarms = currentAlarms.map((alarm: Alarm) => 
        alarm.id === id ? { ...alarm, isActive: !alarm.isActive } : alarm
      );
      const updatedAlarmSets = alarmSets.map((set, index) => 
        index === currentSetIndex 
          ? { ...set, alarms: updatedAlarms }
          : set
      );
      
      setAlarmSets(updatedAlarmSets);
      
      // 알람 활성화 상태 변경 후 즉시 Firestore에 저장
      const saveToggleAlarm = async () => {
        try {
          console.log('🔄 알람 활성화 상태 변경 후 저장 시작:', { userId: user.id, classId: currentClassId, alarmId: id });
          await userDataService.saveClassAlarmSets(user.id, currentClassId, updatedAlarmSets);
          console.log('✅ 알람 활성화 상태 변경 저장 완료');
        } catch (error) {
          console.error('❌ 알람 활성화 상태 변경 후 저장 실패:', error);
          // Firestore 실패 시 로컬 스토리지에 저장
          import('../utils/storage').then(({ saveClassAlarmSets }) => {
            saveClassAlarmSets(updatedAlarmSets, user.id, currentClassId);
          });
        }
      };
      await saveToggleAlarm();
    }
  };

  const deleteAlarm = async (id: string) => {
    // 클래스 ID가 없으면 알람 삭제 불가
    if (!currentClassId) {
      alert('클래스를 먼저 선택해주세요.');
      return;
    }
    
    // 현재 알람 세트에서 알람 삭제
    if (alarmSets.length > 0 && currentSetIndex < alarmSets.length) {
      const updatedAlarms = currentAlarms.filter((alarm: Alarm) => alarm.id !== id);
      const updatedAlarmSets = alarmSets.map((set, index) => 
        index === currentSetIndex 
          ? { ...set, alarms: updatedAlarms }
          : set
      );
      
      setAlarmSets(updatedAlarmSets);
      
      // 삭제 후 즉시 Firestore에 저장
      const saveDeletedAlarm = async () => {
        try {
          console.log('🗑️ 알람 삭제 후 저장 시작:', { userId: user.id, classId: currentClassId, alarmId: id });
          await userDataService.saveClassAlarmSets(user.id, currentClassId, updatedAlarmSets);
          console.log('✅ 알람 삭제 저장 완료');
        } catch (error) {
          console.error('❌ 알람 삭제 후 저장 실패:', error);
          // Firestore 실패 시 로컬 스토리지에 저장
          import('../utils/storage').then(({ saveClassAlarmSets }) => {
            saveClassAlarmSets(updatedAlarmSets, user.id, currentClassId);
          });
        }
      };
      await saveDeletedAlarm();
    }
  };

  const formatAlarmTime = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    
    if (timeFormat === '12h') {
      const period = hours < 12 ? '오전' : '오후';
      const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
      return (
        <span>
          <span style={{ color: '#999', fontSize: '0.8em', marginRight: '4px' }}>
            {period}
          </span>
          {displayHours.toString().padStart(2, '0')}:{minutes.toString().padStart(2, '0')}
        </span>
      );
    } else {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }
  };

  const handleTimeFormatChange = (format: '12h' | '24h') => {
    setTimeFormat(format);
  };

  return (
    <>
      <Header 
        user={user} 
        onLogout={onLogout} 
        onNavigate={onNavigate}
        currentPage="alarm"
        onUpdateUser={onUpdateUser}
        onProfileModalOpen={onProfileModalOpen}
        onClassManageModalOpen={onClassManageModalOpen}
        classes={classes}
        currentClass={currentClass}
        memberGroups={memberGroups}
        currentMemberGroup={currentMemberGroup}
        onMemberGroupChange={onMemberGroupChange}
        themeMode={themeMode}
        onToggleTheme={onToggleTheme}
        onSeatAssignmentClick={onSeatAssignmentClick}
        onLevelSettingsClick={onLevelSettingsClick}
        onPointManagementClick={onPointManagementClick}
        onItemCardSettingsClick={onItemCardSettingsClick}
        onAssignmentSettingsClick={onAssignmentSettingsClick}
      />
      <PageContainer>
      <AlarmSetHeader ref={headerRef}>
                <SetControls>
          <ArrowButton 
            onClick={handlePreviousSet} 
            disabled={currentSetIndex === 0}
            style={{ left: '0px' }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </ArrowButton>
          
          <SetInfo>
            <SetName onDoubleClick={handleEditSetName}>
              {alarmSets.length > 0 ? alarmSets[currentSetIndex]?.name : '새 알람 세트'}
            </SetName>
            {alarmSets.length > 0 && (
              <ActivateButton 
                $isActive={currentSetIndex < alarmSets.length && alarmSets[currentSetIndex]?.isActive}
                $isDisabled={false}
                onClick={() => {
                  if (currentSetIndex < alarmSets.length) {
                    toggleAlarmSetActive(currentSetIndex);
                  }
                }}
              >
                <ActivateToggleOption $isActive={!alarmSets[currentSetIndex]?.isActive} $isDisabled={false}>
                  OFF
                </ActivateToggleOption>
                <ActivateToggleOption $isActive={alarmSets[currentSetIndex]?.isActive} $isDisabled={false}>
                  ON
                </ActivateToggleOption>
              </ActivateButton>
            )}
            <ButtonGroup>
              <DeleteSetButton onClick={handleDeleteAlarmSet}>
                세트 삭제
              </DeleteSetButton>
              <CloneSetButton onClick={handleCloneAlarmSet}>
                세트 복제
              </CloneSetButton>
              <AddSetButton onClick={handleCreateNewAlarmSet}>
                세트 추가
              </AddSetButton>
            </ButtonGroup>
          </SetInfo>
          
          <ArrowButton 
            onClick={handleNextSet} 
            disabled={currentSetIndex >= alarmSets.length - 1}
            style={{ right: '0px' }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </ArrowButton>
        </SetControls>
      </AlarmSetHeader>
      
      <Content $headerHeight={headerHeight}>
        
        <TimeFormatSelector>
          <TimeFormatButton 
            $isSelected={timeFormat === '12h'} 
            onClick={() => handleTimeFormatChange('12h')}
          >
            12H
          </TimeFormatButton>
          <TimeFormatButton 
            $isSelected={timeFormat === '24h'} 
            onClick={() => handleTimeFormatChange('24h')}
          >
            24H
          </TimeFormatButton>
        </TimeFormatSelector>
        <SelectedDayInfo>
          {getDayName(selectedDay)}요일 알람
        </SelectedDayInfo>
        
        <DaySelector>
          {[0, 1, 2, 3, 4, 5, 6].map(dayIndex => (
            <DayButton
              key={dayIndex}
              $isSelected={selectedDay === dayIndex}
              onClick={() => handleDaySelect(dayIndex)}
            >
              {getDayName(dayIndex).charAt(0)}
            </DayButton>
          ))}
        </DaySelector>
        

        
        <AlarmsContainer>
          {filteredAlarms.length > 0 ? (
            <>
              {filteredAlarms.map(alarm => (
                <AlarmBlock
                  key={alarm.id}
                  alarm={alarm}
                  onUpdate={updateAlarm}
                  onToggleActive={toggleAlarmActive}
                  onDelete={deleteAlarm}
                  onCopy={copyAlarm}
                  formatAlarmTime={formatAlarmTime}
                  timeFormat={timeFormat}
                  isSetActive={alarmSets[currentSetIndex]?.isActive || false}
                  isExpanded={expandedAlarmId === alarm.id}
                  onToggle={handleAlarmToggle}
                  user={user}
                  onLicenseClick={onLicenseClick}
                />
              ))}
              <BottomButtonsContainer>
                <DeleteAllAlarmsButton onClick={() => handleDeleteAllAlarmsForDay(selectedDay)}>
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M10 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </DeleteAllAlarmsButton>
                <CopyDayButtonStyled onClick={() => handleCopyDayAlarms(selectedDay)}>
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M8 4v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7.242a2 2 0 0 0-.602-1.43L16.083 2.57A2 2 0 0 0 14.685 2H10a2 2 0 0 0-2 2z" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16 18v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h2" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </CopyDayButtonStyled>
              </BottomButtonsContainer>
            </>
          ) : (
            <EmptyState>
              <EmptyStateTitle>알람이 없습니다</EmptyStateTitle>
              <EmptyStateText>
                {getDayName(selectedDay)}요일에 설정된 알람이 없습니다.
                <br />
                "알람 새로 만들기" 버튼을 눌러 알람을 추가해보세요!
              </EmptyStateText>
            </EmptyState>
          )}
        </AlarmsContainer>

        {/* 하단 EDUKODI 로고 워터마크 (배경보다 아주 조금 진한 회색) */}
        <AlarmPageBottomLogo>
          <img src="/EDUKODI_LOGO2.svg?v=2" alt="" aria-hidden />
        </AlarmPageBottomLogo>
        
        {/* 플로팅 액션 버튼 */}
        <FloatingActionButton onClick={addAlarm}>
          <PlusIcon>+</PlusIcon>
        </FloatingActionButton>
      </Content>

      {/* 삭제 확인 모달 */}
      <ConfirmModal
        isOpen={isDeleteConfirmModalOpen}
        onClose={() => setIsDeleteConfirmModalOpen(false)}
        onConfirm={executeDeleteAlarms}
        title={`${deleteDayIndex !== null ? getDayName(deleteDayIndex) : ''}요일 알람들이 모두 삭제됩니다.\n정말 삭제하시겠습니까?`}
        confirmText="삭제"
        cancelText="취소"
        icon="!"
        iconBackground="var(--status-exempted)"
      />

      {/* NewAlarmModal */}
      <NewAlarmModal
        isOpen={showNewAlarmModal}
        onSave={handleCreateNewAlarm}
        onCancel={handleCancelNewAlarm}
        timeFormat={timeFormat}
        user={user}
        onLicenseClick={onLicenseClick}
      />
      
      {/* SetNameModal (테스트용 - AlarmPage 내부에서 렌더링) */}
      <SetNameModal
        isOpen={showSetNameModal}
        onSave={handleSaveAlarmSet}
        onCancel={handleCancelSetName}
      />

      {/* CloneSetModal */}
      <EditSetNameModal
        isOpen={showCloneSetModal}
        currentName={''}
        onSave={handleSaveCloneSet}
        onCancel={handleCancelCloneSet}
        title="복제할 알람 세트 이름"
      />

              {/* CopyDayModal */}
        <CopyDayModal
          isOpen={showCopyDayModal}
          sourceDay={copySourceDay}
          onSave={handleExecuteCopyDayAlarms}
          onSaveMultiple={handleExecuteCopyDayAlarmsMultiple}
          onCancel={handleCancelCopyDayAlarms}
          getDayName={getDayName}
          onShowMessage={(message, isError = false) => {
            setCopySuccessMessage(message);
            setIsDuplicateMessage(isError);
            setIsCopySuccessModalOpen(true);
          }}
        />

        {/* 복사 완료 메시지 팝업 */}
        {isCopySuccessModalOpen && (
          <CopySuccessOverlay onClick={() => setIsCopySuccessModalOpen(false)}>
            <CopySuccessContainer onClick={(e) => e.stopPropagation()}>
              <CopySuccessContent>
                <CopySuccessIcon style={{ background: isDuplicateMessage ? '#ff6b6b' : 'var(--color-accent-strong)' }}>
                  {isDuplicateMessage ? '⚠' : '✓'}
                </CopySuccessIcon>
                <CopySuccessTitle>{copySuccessMessage}</CopySuccessTitle>
              </CopySuccessContent>
              <CopySuccessFooter>
                <CloseAfterCopyButton data-variant="modal-secondary" onClick={handleCloseAfterCopy}>
                  닫기
                </CloseAfterCopyButton>
              </CopySuccessFooter>
            </CopySuccessContainer>
          </CopySuccessOverlay>
        )}



        {/* SetNameModal */}
        <SetNameModal
          isOpen={showSetNameModal}
          onSave={handleSaveAlarmSet}
          onCancel={handleCancelSetName}
        />

        {/* EditSetNameModal for Clone */}
        <EditSetNameModal
          isOpen={showCloneSetModal}
          currentName={''}
          onSave={handleSaveCloneSet}
          onCancel={handleCancelCloneSet}
          title="복제할 알람 세트 이름"
        />
        
        {/* EditSetNameModal for Edit */}
        <EditSetNameModal
          isOpen={showEditSetNameModal}
          currentName={alarmSets.length > 0 && currentSetIndex < alarmSets.length ? alarmSets[currentSetIndex].name : ''}
          onSave={handleSaveEditSetName}
          onCancel={handleCancelEditSetName}
          title="알람 세트 이름 수정"
        />

        <LicenseLimitModal
          isOpen={showLicenseLimitModal}
          message={licenseLimitMessage}
          onClose={() => setShowLicenseLimitModal(false)}
          onUpgrade={() => {
            setShowLicenseLimitModal(false);
            onLicenseClick?.();
          }}
        />
      </PageContainer>
    </>
  );
};

export default AlarmPage; 